// The homepage's layout: which cards show, in what order and size, and which
// stickers go where. Shared by the homepage and the "stickers & cards"
// settings page. Saved on the server (so it's the same on every device) and
// kept in this browser too, so the homepage paints the right way instantly.

export const BUILTIN_STICKERS = [
  { id: 'night', caption: 'late night, one more idea', rotation: true },
  { id: 'peek', caption: 'peekaboo', rotation: true },
  { id: 'feed', caption: 'one bite for you', rotation: true },
  { id: 'grumpy', caption: 'that was MY cheesecake', rotation: true },
  { id: 'idea', caption: 'system compiled!', rotation: true },
  { id: 'pat', caption: 'good girl', rotation: true },
  { id: 'study', caption: 'studying together', rotation: false },
];

export const CARD_DEFS = [
  { key: 'study', path: '/study', title: 'study', sub: 'focus with caleb beside you' },
  { key: 'novels', path: '/novels', title: 'novels', sub: 'write, translate, export' },
  { key: 'writing', path: '/writing', title: 'writing', sub: 'premise in, story out' },
  { key: 'todos', path: '/todos', title: 'to-do', sub: "everything you're carrying" },
  { key: 'video', path: '/video', title: 'video', sub: 'dream it, generate it' },
  { key: 'companions', path: '/companions/list', title: 'companions', sub: 'chats, calls, journals' },
  { key: 'fanfiction', path: '/fanfiction', title: 'fanfiction', sub: 'what you read, what you thought' },
  { key: 'vault', path: '/vault', title: 'vault', sub: 'everything you keep' },
];

export const TIME_SLOTS = [
  { key: 'morning', label: 'morning', hint: '5am – 11am' },
  { key: 'afternoon', label: 'afternoon', hint: '11am – 5pm' },
  { key: 'evening', label: 'evening', hint: '5pm – 10pm' },
  { key: 'night', label: 'late night', hint: '10pm – 5am' },
];

export const DEFAULT_LAYOUT = {
  version: 1,
  cards: [
    { key: 'study', size: 'full', show: true },
    { key: 'novels', size: 'half', show: true },
    { key: 'writing', size: 'half', show: true },
    { key: 'todos', size: 'half', show: true },
    { key: 'video', size: 'half', show: true },
    { key: 'companions', size: 'full', show: true },
    { key: 'fanfiction', size: 'half', show: true },
    { key: 'vault', size: 'half', show: true },
  ],
  stickers: BUILTIN_STICKERS.map(s => ({ ...s, url: null, builtin: true })),
  greeting: { morning: 'peek', afternoon: 'feed', evening: 'pat', night: 'night' },
  card_stickers: { study: 'study', novels: 'night', writing: 'feed', todos: 'idea', video: 'grumpy', companions: 'pat', fanfiction: null, vault: null },
  peeker: 'peek',
};

const clone = (x) => JSON.parse(JSON.stringify(x));

// Fill in anything missing (a card added to the site later, a built-in
// sticker) and drop anything that no longer exists, so an old saved layout
// always works.
export function normalizeLayout(raw) {
  const d = clone(DEFAULT_LAYOUT);
  if (!raw || typeof raw !== 'object') return d;
  const known = new Set(CARD_DEFS.map(c => c.key));
  const cards = [];
  (Array.isArray(raw.cards) ? raw.cards : []).forEach(c => {
    if (c && known.has(c.key) && !cards.some(x => x.key === c.key)) {
      cards.push({ key: c.key, size: c.size === 'full' ? 'full' : 'half', show: c.show !== false });
    }
  });
  d.cards.forEach(c => { if (!cards.some(x => x.key === c.key)) cards.push(c); });

  const stickers = [];
  (Array.isArray(raw.stickers) ? raw.stickers : []).forEach(s => {
    if (s && s.id && !stickers.some(x => x.id === s.id)) {
      stickers.push({ id: String(s.id), url: s.url || null, caption: s.caption || '', builtin: !!s.builtin, rotation: s.rotation !== false });
    }
  });
  d.stickers.forEach(s => { if (!stickers.some(x => x.id === s.id)) stickers.push(s); });
  const ids = new Set(stickers.map(s => s.id));
  const pick = (v, fallback) => (v === null ? null : ids.has(v) ? v : fallback);

  const greeting = {};
  TIME_SLOTS.forEach(t => { greeting[t.key] = pick((raw.greeting || {})[t.key], d.greeting[t.key]) || d.greeting[t.key]; });
  const cardStickers = {};
  CARD_DEFS.forEach(c => {
    const v = (raw.card_stickers || {})[c.key];
    cardStickers[c.key] = v === undefined ? d.card_stickers[c.key] : pick(v, d.card_stickers[c.key]);
  });
  return {
    version: 1, cards, stickers, greeting, card_stickers: cardStickers,
    peeker: raw.peeker === undefined ? d.peeker : pick(raw.peeker, d.peeker),
  };
}

export const stickerSrc = (s) => (s ? (s.url || `/stickers/${s.id}.webp`) : null);
export const findSticker = (layout, id) => (id ? layout.stickers.find(s => s.id === id) || null : null);

export const timeSlot = (h = new Date().getHours()) =>
  (h >= 22 || h < 5 ? 'night' : h < 11 ? 'morning' : h < 17 ? 'afternoon' : 'evening');

const CACHE = 'homeLayout';
export const cachedLayout = () => {
  try { const v = localStorage.getItem(CACHE); return normalizeLayout(v ? JSON.parse(v) : null); } catch (e) { return normalizeLayout(null); }
};
export const cacheLayout = (layout) => {
  try { localStorage.setItem(CACHE, JSON.stringify(layout)); } catch (e) { /* fine */ }
};
